<template>
    <div>
        <div class="ppv2-space-y-4">
            <ConfigurationInlineCard
                v-for="card in configurationInlineCards"
                :key="card.id"
                :iconSrc="card.iconSrc"
                :chevronSrc="card.chevronSrc"
                :title="card.title"
                :description="card.description"
                :statusText="card.statusText"
                :statusColor="card.statusColor"
                :class="{ 'ppv2-border-secondary-500 ppv2-border ppv2-bg-secondary-100': card.isActive }"
                @card-action="handleCardAction(card)"
            />
        </div>

        <!-- Dynamic Form Panel -->
        <PanelComponent
            v-if="showPanel && selectedCard && panelComponents[selectedCard.id]"
            :title="selectedCard.title"
            :description="selectedCard.description"
            :cancelText="$t('common.cancel')"
            :validateText="$t('common.save')"
            @close="closePanel"
            @cancel="closePanel(false)"
            @outside-close="closePanel(false)"
            @validate="submitForm"
            :disabledConfirm="!isFormValid"
        >
            <component
                ref="activeForm"
                :is="panelComponents[selectedCard.id]"
                :card="selectedCard"
                :website-setting="websiteSetting"
                :app-setting="appSetting"
                :establishment="establishment"
                :countries="countries"
                :currencies="currencies"
                :gift-enabled="giftEnabled"
                :server-ip="serverIp"
                @update="updateEstablishment"
                @is-valid="handleFormValidity"
            />
        </PanelComponent>
    </div>
</template>

<script>
import ConfigurationInlineCard from '../../../Shared/ConfigurationInlineCard.vue'
import PanelComponent from '../../../Shared/PanelComponent.vue'
import establishmentMixin from '../../Mixins/establishmentMixin'
import { EventBus } from '../../../../eventBus.js'

// Form Components
import PaymentMethods from './PaymentMethods.vue'
import StripeSetup from './StripeSetup.vue'
import PaymentPrepaidSolutions from './PaymentPrepaidSolutions.vue'
import DepositForm from './DepositForm.vue'
import BillingPolicy from './BillingPolicy.vue'
import DevisForm from './DevisForm.vue'
import BankDetails from './BankDetails.vue'
import Wallet from './Wallet.vue'
import LoyaltyProgram from './LoyaltyProgram.vue'
import InvoiceFooter from './InvoiceFooter.vue'

export default {
    name: 'FinancialBilling',
    mixins: [establishmentMixin],
    props: {
        websiteSetting: {
            required: true,
        },
        appSetting: {
            required: true,
        },
        countries: {
            required: true,
        },
        currencies: {
            required: true,
        },
        giftEnabled: {
            type: Boolean,
            default: false,
        },
        serverIp: {
            type: String,
            default: '',
        },
    },
    components: {
        ConfigurationInlineCard,
        PanelComponent,
        PaymentMethods,
        StripeSetup,
        PaymentPrepaidSolutions,
        DepositForm,
        BillingPolicy,
        DevisForm,
        BankDetails,
        Wallet,
        LoyaltyProgram,
        InvoiceFooter,
    },
    data() {
        return {
            showPanel: false,
            isFormValid: true,
            selectedCard: null,
            cards: [
                {
                    id: 1,
                    key: 'PaymentMethods',
                    titleKey: 'configuration.inlineCards.billing.title',
                    descriptionKey: 'configuration.inlineCards.billing.description',
                    iconSrc: '/v2/images/img_group_1000004856.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    canShow: true,
                },
                {
                    id: 2,
                    key: 'stripeSetup',
                    titleKey: 'configuration.inlineCards.stripeSetup.title',
                    descriptionKey: 'configuration.inlineCards.stripeSetup.description',
                    iconSrc: '/v2/images/img_group_1000004856.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    canShow: true,
                },
                {
                    id: 3,
                    key: 'paymentPrepaidSolutions',
                    titleKey: 'configuration.inlineCards.paymentPrepaidSolutions.title',
                    descriptionKey: 'configuration.inlineCards.paymentPrepaidSolutions.description',
                    iconSrc: '/v2/images/img_group_1000004856.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    canShow: true,
                },
                {
                    id: 4,
                    key: 'deposit',
                    titleKey: 'configuration.inlineCards.deposit.title',
                    descriptionKey: 'configuration.inlineCards.deposit.description',
                    iconSrc: '/v2/images/img_group_1000004856.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    canShow: true,
                },
                {
                    id: 5,
                    key: 'billingPolicy',
                    titleKey: 'configuration.inlineCards.billingPolicy.title',
                    descriptionKey: 'configuration.inlineCards.billingPolicy.description',
                    iconSrc: '/v2/images/img_group_1000004856.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    canShow: true,
                },
                {
                    id: 10,
                    key: 'devis',
                    titleKey: 'configuration.inlineCards.devis.title',
                    descriptionKey: 'configuration.inlineCards.devis.description',
                    iconSrc: '/v2/images/img_group_1000004856.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    // MASQUÉE VOLONTAIREMENT — le devis se compose pour l'instant uniquement
                    // SANS blocage de créneau (make_devis_with_time_slot = 0), et le mode est
                    // FIGÉ sur chaque devis à sa création : laisser basculer le réglage en
                    // cours de route produirait des devis dont une partie des lignes bloque des
                    // ressources et l'autre non.
                    //
                    // La carte, son formulaire (DevisForm) et la persistance restent en place :
                    // repasser `canShow` à true suffit à rendre la main aux utilisateurs.
                    canShow: false,
                },
                {
                    id: 6,
                    key: 'bankDetails',
                    titleKey: 'configuration.inlineCards.bankDetails.title',
                    descriptionKey: 'configuration.inlineCards.bankDetails.description',
                    iconSrc: '/v2/images/img_group_1000004856.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    canShow: true,
                },
                {
                    id: 7,
                    key: 'wallet',
                    titleKey: 'configuration.inlineCards.wallet.title',
                    descriptionKey: 'configuration.inlineCards.wallet.description',
                    iconSrc: '/v2/images/img_group_1000004856.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    canShow: true,
                },
                {
                    id: 8,
                    key: 'loyaltyProgram',
                    titleKey: 'configuration.inlineCards.loyaltyProgram.title',
                    descriptionKey: 'configuration.inlineCards.loyaltyProgram.description',
                    iconSrc: '/v2/images/img_group_1000004856.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    canShow: true,
                },
                {
                    id: 9,
                    key: 'invoiceFooter',
                    titleKey: 'configuration.inlineCards.invoiceFooter.title',
                    descriptionKey: 'configuration.inlineCards.invoiceFooter.description',
                    iconSrc: '/v2/images/img_group_1000004856.svg',
                    chevronSrc: '/v2/images/img_chevronright.svg',
                    canShow: true,
                },
            ],
        }
    },
    computed: {
        establishment() {
            return this.$store.getters['establishment/establishment']
        },
        configurationInlineCards() {
            return this.cards
                .filter((card) => card.canShow)
                .map((card) => ({
                    ...card,
                    title: this.$t(card.titleKey),
                    description: this.$t(card.descriptionKey),
                    isActive: this.selectedCard?.id === card.id,
                }))
        },
        panelComponents() {
            return {
                1: 'PaymentMethods',
                2: 'StripeSetup',
                3: 'PaymentPrepaidSolutions',
                4: 'DepositForm',
                5: 'BillingPolicy',
                6: 'BankDetails',
                7: 'Wallet',
                8: 'LoyaltyProgram',
                9: 'InvoiceFooter',
                10: 'DevisForm',
            }
        },
    },
    methods: {
        handleFormValidity(isValid) {
            this.isFormValid = isValid
        },
        closePanel(value) {
            this.showPanel = false
            this.isFormValid = true // Reset validity when closing
        },
        handleOpenSection(sectionKey) {
            const card = this.cards.find((c) => c.key === sectionKey)
            if (card) {
                this.handleCardAction(card)
            }
        },
    },
    created() {
        EventBus.$on('open-establishment-section', this.handleOpenSection)

        // Also check if we should open a section on load
        if (window.location.hash.includes(':')) {
            const subHash = window.location.hash.split(':')[1]
            this.$nextTick(() => {
                this.handleOpenSection(subHash)
            })
        }
    },
    beforeDestroy() {
        EventBus.$off('open-establishment-section', this.handleOpenSection)
    },
}
</script>
